import { RelativeTimestamp } from "~/components/RelativeTimestamp"
import {
	type ComponentProps,
	type CSSProperties,
	type ReactNode,
	useRef
} from "react"
import { toast } from "sonner"
import { appShortcuts, isModifiedShortcut } from "~/lib/keyboard-shortcuts"
import { Editor } from "~/components/editor/Editor"
import { ContentTemplateKind } from "~/lib/enums"
import type { MentionAutocompleteUser } from "~/components/editor/plugins/MentionAutocomplete/utils"
import { EntityIdentity } from "~/components/EntityIdentity"
import type { UserHoverCardMember } from "~/components/UserHoverCard"

import { Button } from "~/components/ui/button"

import {
	cn,
	getEditorPlainText,
	normalizeEditorContent,
	textToEditorContent
} from "~/lib/utils"

export function CommentSurface({
	resolved,
	children,
	className,
	...props
}: ComponentProps<"div"> & { resolved?: boolean }) {
	return (
		<div
			className={cn(
				"group/comment relative min-w-0 max-w-full overflow-hidden rounded-md border border-stroke bg-background-secondary transition-colors hover:border-stroke-hover",
				resolved && "border-l-green-500 bg-background-secondary/70",
				className
			)}
			{...props}
		>
			{resolved && (
				<div
					aria-hidden
					className="absolute left-0 top-0 bottom-0 w-1 bg-green-500"
				/>
			)}
			{children}
		</div>
	)
}

export function CommentHeader(props: ComponentProps<"div">) {
	return (
		<div
			className="flex min-h-12 min-w-0 items-center gap-2 px-3 py-2.5 pl-4"
			{...props}
		/>
	)
}

export function CommentBody(props: ComponentProps<"div">) {
	return <div className="min-w-0 px-3 pb-3 pl-4" {...props} />
}

export function ActivityTimelineItem({
	isFirst,
	isLast,
	variant,
	children,
	...props
}: ComponentProps<"div"> & ComponentProps<typeof ActivityTimelineRail>) {
	return (
		<div className="relative min-w-0 max-w-full pl-10" {...props}>
			<ActivityTimelineRail
				isFirst={isFirst}
				isLast={isLast}
				variant={variant}
			/>
			{children}
		</div>
	)
}

export function ActivityTimelineRail({
	isFirst,
	isLast,
	variant
}: {
	isFirst: boolean
	isLast: boolean
	variant: "event" | "comment"
}) {
	const markerCenter = variant === "comment" ? "1.5rem" : "0.875rem"
	const lineStyle: CSSProperties = {
		top: isFirst ? markerCenter : "-0.75rem",
		bottom: isLast ? `calc(100% - ${markerCenter})` : "-0.75rem"
	}
	const markerStyle: CSSProperties = {
		top: `calc(${markerCenter} - 0.375rem)`
	}

	return (
		<div
			aria-hidden
			className="pointer-events-none absolute top-0 bottom-0 left-0 w-8"
		>
			<div
				className="absolute left-4 w-px -translate-x-1/2 bg-stroke"
				style={lineStyle}
			/>
			<div
				className={cn(
					"absolute left-4 size-3 -translate-x-1/2 rounded-full border-2 bg-background",
					variant === "comment"
						? "border-orbit"
						: "border-stroke bg-background-secondary"
				)}
				style={markerStyle}
			/>
		</div>
	)
}

export function CommentComposer({
	value,
	onChange,
	onSubmit,
	placeholder,
	disabled,
	isPending,
	submitLabel,
	className,
	mentionUsers,
	referencesEnabled = true
}: {
	value: string
	onChange: (value: string) => void
	onSubmit: () => Promise<void>
	placeholder: string
	disabled?: boolean
	isPending?: boolean
	submitLabel: string
	className?: string
	mentionUsers?: MentionAutocompleteUser[]
	referencesEnabled?: boolean
}) {
	const hasContent = getEditorPlainText(value).trim().length > 0
	const submitting = useRef(false)
	const submit = async () => {
		if (!hasContent || disabled || isPending || submitting.current) return
		submitting.current = true
		try {
			await onSubmit()
			onChange(textToEditorContent(null))
		} catch (error) {
			toast.error(
				error instanceof Error ? error.message : "Failed to post comment"
			)
		} finally {
			submitting.current = false
		}
	}

	return (
		<div
			className={cn("flex flex-col gap-2", className)}
			onKeyDownCapture={(event) => {
				// Handle submission before the editor and its table keymaps.
				if (
					!isModifiedShortcut(
						event.nativeEvent,
						appShortcuts.submit_comment.keys
					) ||
					event.nativeEvent.isComposing ||
					event.altKey ||
					event.shiftKey
				)
					return
				event.preventDefault()
				event.stopPropagation()
				if (!event.repeat) void submit()
			}}
		>
			<Editor
				compact
				placeholder={placeholder}
				initialState={value}
				onChange={onChange}
				disabled={disabled || isPending}
				variant="composer"
				templateKind={
					referencesEnabled ? ContentTemplateKind.Comment : undefined
				}
				referencesEnabled={referencesEnabled}
				mentionUsers={mentionUsers}
			/>
			<div className="flex justify-end">
				<Button
					aria-keyshortcuts="Control+Enter Meta+Enter"
					onClick={() => void submit()}
					disabled={disabled || isPending || !hasContent}
				>
					{isPending ? "Posting..." : submitLabel}
				</Button>
			</div>
		</div>
	)
}

export function CommentContent({
	content,
	referencesEnabled = true
}: {
	content: string
	referencesEnabled?: boolean
}) {
	return (
		<Editor
			initialState={normalizeEditorContent(content)}
			referencesEnabled={referencesEnabled}
			mentionUsers={referencesEnabled ? undefined : []}
			disabled
			variant="comment"
		/>
	)
}

export function ActivityCommentCard({
	id,
	content,
	authorName,
	authorImage,
	createdAt,
	formatRelativeTimestamp,
	member,
	badges,
	children,
	className
}: {
	id?: string
	content: string
	authorName: string
	authorImage?: string | null
	createdAt?: Date
	formatRelativeTimestamp?: (date: Date) => string
	member?: UserHoverCardMember
	badges?: ReactNode
	children?: ReactNode
	className?: string
}) {
	return (
		<CommentSurface id={id} className={className}>
			<CommentHeader>
				<div className="min-w-0 flex-1">
					<div className="flex min-w-0 flex-wrap items-center gap-2">
						<EntityIdentity
							kind="user"
							member={member}
							name={authorName}
							image={authorImage ?? "/logo.png"}
							lastSeenAt={member?.lastSeenAt}
							size="md"
						/>
						{badges}
						{createdAt && (
							<RelativeTimestamp
								date={createdAt}
								formatRelativeTimestamp={formatRelativeTimestamp}
							/>
						)}
					</div>
				</div>
			</CommentHeader>
			<CommentBody>
				<CommentContent content={content} />
				{children}
			</CommentBody>
		</CommentSurface>
	)
}
